import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

type Settings = {
  enabled: boolean;
  homeTenantId: string;
  uamiClientId: string;
  uamiPrincipalId: string;
  uamiResourceId: string;
  brokerAppId: string;
  brokerObjectId: string;
};

type TokenDiagnostics = {
  acquired?: boolean;
  tenantId?: string;
  appId?: string;
  audience?: string;
  expiresAt?: string;
  roles?: string[];
  error?: string;
};

type ManagedIdentityDiagnostics = {
  acquired?: boolean;
  requestedResource?: string;
  observedAudience?: string;
  audienceResolution?: string;
  issuer?: string;
  tenantId?: string;
  objectId?: string;
  subject?: string;
  clientId?: string;
  expiresAt?: string;
  error?: string;
};

type ProviderResult = {
  ok?: boolean;
  diagnostics?: TokenDiagnostics;
  permissions?: { expected?: string[]; missing?: string[]; ready?: boolean };
  probe?: { ok?: boolean; status?: string; message?: string; details?: Record<string, unknown> };
  error?: string;
};

type SourceReadiness = Record<string, { ready: boolean; reason: string }>;

type TestResult = {
  overall?: string;
  testedAt?: string;
  targetTenantId?: string;
  managedIdentity?: ManagedIdentityDiagnostics;
  graph?: ProviderResult;
  mde?: ProviderResult;
  intune?: ProviderResult;
  azure?: ProviderResult;
  sentinel?: ProviderResult;
  sentinelLogs?: ProviderResult;
  sourceReadiness?: SourceReadiness;
  error?: string;
};

type Connection = {
  customerId: string;
  customerName: string;
  tenantId: string;
  subscription?: string | null;
  resourceGroup?: string | null;
  workspaceName?: string | null;
  workspaceId?: string | null;
  customerEnabled: boolean;
  enabled: boolean;
  graphEnabled: boolean;
  mdeEnabled: boolean;
  intuneEnabled: boolean;
  azureEnabled: boolean;
  sentinelEnabled: boolean;
  sentinelLogsEnabled: boolean;
  lastTestedAt?: string | null;
  lastTestStatus?: string | null;
  lastTest?: TestResult | null;
  homeTenant: boolean;
};

type History = {
  id: string;
  customerId: string;
  status: string;
  requestedByEmail?: string | null;
  completedAt?: string;
  result?: TestResult;
};

type EstateAdapterHealth = {
  adapterId: string;
  label: string;
  provider: string;
  category: string;
  status: string;
  rowCount: number;
  relationshipCount: number;
  durationMs: number;
  truncated: boolean;
  message: string;
  sourceTable?: string | null;
};

type EstateRun = {
  id: string;
  customerId: string;
  customerName: string;
  status: string;
  requestedByEmail?: string | null;
  lookbackHours: number;
  adapterRegistryVersion: string;
  startedAt?: string | null;
  completedAt?: string | null;
  summary?: Record<string, number>;
  sourceHealth?: EstateAdapterHealth[];
  diagnostics?: string[];
};

type EstateKnowledgeGraphSummary = {
  rawObservations?: number;
  rawRelationships?: number;
  canonicalEntities?: number;
  canonicalRelationships?: number;
  reductionPercent?: number;
  exactIdMerges?: number;
  crossProviderEntities?: number;
  hostnameFallbackMerges?: number;
  unresolvedEntities?: number;
  ambiguousCandidates?: number;
  conflicts?: number;
  resolvedRelationships?: number;
  unresolvedRelationships?: number;
  ambiguousRelationships?: number;
  relationshipResolutionPercent?: number;
  entityTypeCounts?: Record<string, number>;
  providerCoverageCounts?: Record<string, number>;
};

type EstateKnowledgeGraphRun = {
  id: string;
  customerId: string;
  customerName: string;
  sourceDiscoveryRunId: string;
  status: string;
  resolverVersion: string;
  requestedByEmail?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  summary?: EstateKnowledgeGraphSummary;
  diagnostics?: string[];
};

type EstateKnowledgeGraphEntitySample = {
  id: string;
  entityType: string;
  displayName: string;
  identifiers?: Record<string, unknown>;
  providers?: string[];
  confidence?: number;
  matchStrategy?: string;
  evidenceCount?: number;
  authoritativeEvidenceCount?: number;
  conflicts?: unknown[];
  evidence?: Array<{ provider?: string; source?: string; sourceTable?: string | null; authority?: string; matchStrategy?: string; matchKey?: string | null }>;
};

type EstateKnowledgeGraphRelationshipSample = {
  id: string;
  fromEntityId: string;
  toEntityId: string;
  relationshipType: string;
  authority?: string;
  confidence?: number;
  evidenceCount?: number;
  providers?: string[];
  evidence?: Array<{ provider?: string; source?: string; sourceTable?: string | null; authority?: string }>;
};

type EstateKnowledgeGraphSample = {
  entities: EstateKnowledgeGraphEntitySample[];
  relationships: EstateKnowledgeGraphRelationshipSample[];
};

type AdapterManifest = {
  id: string;
  label: string;
  provider: string;
  category: string;
  sourceTable?: string | null;
  registryVersion?: string;
};

type MasterResponse = {
  ok: boolean;
  stage: string;
  mode: string;
  secretsStored: number;
  settings: Settings;
  connections: Connection[];
  history: History[];
  estateRuntimeEnabled?: boolean;
  estateKnowledgeGraphEnabled?: boolean;
  adapterRegistry?: AdapterManifest[];
  estateRuns?: EstateRun[];
  estateKnowledgeGraphRuns?: EstateKnowledgeGraphRun[];
};

const SOURCE_LABELS: Record<string, string> = {
  entra_users: 'Entra Users',
  entra_devices: 'Entra Devices',
  entra_groups: 'Groups & Memberships',
  entra_applications: 'Apps / Service Principals',
  entra_signins_audit: 'Sign-ins & Audit Logs',
  entra_risky_users: 'Risky Users',
  entra_directory_roles: 'Directory Roles',
  entra_conditional_access: 'Conditional Access',
  intune_devices: 'Intune Managed Devices',
  mde_machines: 'MDE Machines',
  azure_resources: 'Azure Resource Graph',
  sentinel_incidents: 'Sentinel Incidents',
  sentinel_logs: 'Sentinel / Log Analytics',
};

function badge(status?: string | null) {
  const s = String(status ?? 'untested').toLowerCase();
  const cls = s === 'ready' || s === 'succeeded'
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
    : s === 'running'
      ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200'
      : s === 'partial'
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
      : s === 'failed' || s === 'error'
        ? 'border-rose-500/30 bg-rose-500/10 text-rose-300'
        : 'border-border bg-muted/30 text-muted-foreground';
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>{s}</span>;
}

function managedIdentityCard(value?: ManagedIdentityDiagnostics) {
  const status = value ? (value.acquired ? 'ready' : 'failed') : 'untested';
  return (
    <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 xl:col-span-3">
      <div className="flex items-center justify-between gap-2"><div className="text-xs font-bold">Discovery Managed Identity Assertion</div>{badge(status)}</div>
      {value?.error ? <div className="mt-2 text-xs text-rose-300 break-words">{value.error}</div> : null}
      {value ? (
        <div className="mt-2 grid gap-x-4 gap-y-1 text-[11px] text-muted-foreground md:grid-cols-2 xl:grid-cols-3">
          <div>Requested resource: <span className="font-mono text-foreground break-all">{value.requestedResource ?? '—'}</span></div>
          <div>JWT audience: <span className="font-mono text-foreground break-all">{value.observedAudience ?? '—'}</span></div>
          <div>Resolution: <span className={value.acquired ? 'text-emerald-300' : 'text-amber-300'}>{value.audienceResolution ?? '—'}</span></div>
          <div>Tenant: <span className="font-mono text-foreground break-all">{value.tenantId ?? '—'}</span></div>
          <div>UAMI client ID: <span className="font-mono text-foreground break-all">{value.clientId ?? '—'}</span></div>
          <div>UAMI principal/object ID: <span className="font-mono text-foreground break-all">{value.objectId ?? value.subject ?? '—'}</span></div>
          <div className="md:col-span-2">Issuer: <span className="font-mono text-foreground break-all">{value.issuer ?? '—'}</span></div>
          <div>Expires: <span className="text-foreground">{value.expiresAt ? new Date(value.expiresAt).toLocaleString() : '—'}</span></div>
          <div className="md:col-span-2 xl:col-span-3 text-emerald-300">Discovery assertion/tokens remain backend-memory only; no secret, assertion or token is returned to the browser or persisted.</div>
        </div>
      ) : null}
    </div>
  );
}

function providerCard(label: string, value?: ProviderResult) {
  const roles = value?.diagnostics?.roles ?? [];
  const missing = value?.permissions?.missing ?? [];
  return (
    <div className="rounded-xl border border-border bg-background/40 p-3">
      <div className="flex items-center justify-between gap-2"><div className="text-xs font-bold">{label}</div>{badge(value ? (value.ok ? 'ready' : 'failed') : 'untested')}</div>
      {value?.error ? <div className="mt-2 text-xs text-rose-300 break-words">{value.error}</div> : null}
      {value?.diagnostics ? (
        <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
          <div>Tenant: <span className="font-mono text-foreground">{value.diagnostics.tenantId ?? '—'}</span></div>
          <div>Audience: <span className="font-mono text-foreground break-all">{value.diagnostics.audience ?? '—'}</span></div>
          <div>Expires: <span className="text-foreground">{value.diagnostics.expiresAt ? new Date(value.diagnostics.expiresAt).toLocaleString() : '—'}</span></div>
          {roles.length ? <div className="pt-1">App roles: {roles.map((r) => <span key={r} className="mr-1 mt-1 inline-flex rounded border border-cyan-500/20 bg-cyan-500/10 px-1.5 py-0.5 font-mono text-cyan-200">{r}</span>)}</div> : null}
          {missing.length ? <div className="text-amber-300">Missing: {missing.join(', ')}</div> : null}
          {value.probe?.message ? <div className={value.probe.ok ? 'text-emerald-300' : 'text-amber-300'}>{value.probe.message}</div> : null}
        </div>
      ) : value?.probe?.message ? <div className={value.probe.ok ? 'mt-2 text-[11px] text-emerald-300' : 'mt-2 text-[11px] text-amber-300'}>{value.probe.message}</div> : null}
    </div>
  );
}

export function DiscoveryConnectionMaster() {
  const { canCapability } = useAuth();
  const canManageSettings = canCapability('discovery_connection_master', 'manage_settings');
  const canManageConnections = canCapability('discovery_connection_master', 'manage_customer_connections');
  const canTest = canCapability('discovery_connection_master', 'test_authentication');
  const canViewHistory = canCapability('discovery_connection_master', 'view_test_history');
  const canRunEstate = canCapability('discovery_connection_master', 'run_estate_discovery');
  const canViewEstate = canCapability('discovery_connection_master', 'view_estate_discovery');
  const canBuildKnowledgeGraph = canCapability('discovery_connection_master', 'build_estate_knowledge_graph');
  const canViewKnowledgeGraph = canCapability('discovery_connection_master', 'view_estate_knowledge_graph');
  const canViewKnowledgeGraphEvidence = canCapability('discovery_connection_master', 'view_estate_knowledge_graph_evidence');

  const [data, setData] = useState<MasterResponse | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [knowledgeGraphSample, setKnowledgeGraphSample] = useState<EstateKnowledgeGraphSample | null>(null);
  const [knowledgeGraphSampleRunId, setKnowledgeGraphSampleRunId] = useState<string | null>(null);

  const load = async () => {
    setError('');
    try {
      const r = await apiFetch<MasterResponse>('/discovery-broker/master');
      setData(r);
      setSettings(r.settings);
      setSelected((current) => {
        if (current && r.connections.some((c) => c.customerId === current)) return current;
        return r.connections.find((c) => c.homeTenant)?.customerId ?? r.connections[0]?.customerId ?? null;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void load(); }, []);

  const selectedConnection = useMemo(() => data?.connections.find((c) => c.customerId === selected) ?? null, [data, selected]);
  useEffect(() => { setKnowledgeGraphSample(null); setKnowledgeGraphSampleRunId(null); }, [selected]);
  const totals = useMemo(() => {
    const rows = data?.connections ?? [];
    return {
      total: rows.length,
      enabled: rows.filter((r) => r.enabled).length,
      ready: rows.filter((r) => r.lastTestStatus === 'ready').length,
      partial: rows.filter((r) => r.lastTestStatus === 'partial').length,
      failed: rows.filter((r) => r.lastTestStatus === 'failed').length,
    };
  }, [data]);

  const saveSettings = async () => {
    if (!settings || !canManageSettings) return;
    setBusy('settings'); setError(''); setMessage('');
    try {
      await apiFetch('/discovery-broker/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings) });
      setMessage('Discovery Broker settings saved. Only non-secret identifiers are stored.');
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const patchConnection = async (row: Connection, patch: Partial<Pick<Connection, 'enabled'|'graphEnabled'|'mdeEnabled'|'intuneEnabled'|'azureEnabled'|'sentinelEnabled'|'sentinelLogsEnabled'>>) => {
    if (!canManageConnections) return;
    setBusy(`connection:${row.customerId}`); setError(''); setMessage('');
    try {
      await apiFetch(`/discovery-broker/connections/${encodeURIComponent(row.customerId)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: patch.enabled ?? row.enabled,
          graphEnabled: patch.graphEnabled ?? row.graphEnabled,
          mdeEnabled: patch.mdeEnabled ?? row.mdeEnabled,
          intuneEnabled: patch.intuneEnabled ?? row.intuneEnabled,
          azureEnabled: patch.azureEnabled ?? row.azureEnabled,
          sentinelEnabled: patch.sentinelEnabled ?? row.sentinelEnabled,
          sentinelLogsEnabled: patch.sentinelLogsEnabled ?? row.sentinelLogsEnabled,
        }),
      });
      setMessage(`Discovery connection updated for ${row.customerName}.`);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const testConnection = async (row: Connection) => {
    if (!canTest) return;
    setBusy(`test:${row.customerId}`); setError(''); setMessage(''); setSelected(row.customerId);
    try {
      const r = await apiFetch<{ ok: boolean; result: TestResult }>(`/discovery-broker/connections/${encodeURIComponent(row.customerId)}/test`, { method: 'POST' });
      setMessage(`Discovery authentication/readiness test completed for ${row.customerName}: ${String(r.result.overall ?? 'unknown').toUpperCase()}.`);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const runEstateDiscovery = async (row: Connection) => {
    if (!canRunEstate || !data?.estateRuntimeEnabled) return;
    setBusy(`estate:${row.customerId}`); setError(''); setMessage(''); setSelected(row.customerId);
    try {
      const r = await apiFetch<{ ok: boolean; run: EstateRun }>(`/discovery-broker/connections/${encodeURIComponent(row.customerId)}/discover`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lookbackHours: 72 }),
      });
      const obs = Number(r.run.summary?.observations ?? 0);
      const rel = Number(r.run.summary?.relationships ?? 0);
      setMessage(`Estate discovery completed for ${row.customerName}: ${String(r.run.status ?? 'unknown').toUpperCase()} · ${obs} observations · ${rel} relationships.`);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const buildKnowledgeGraph = async (row: Connection) => {
    if (!canBuildKnowledgeGraph || !data?.estateKnowledgeGraphEnabled) return;
    setBusy(`knowledge-graph:${row.customerId}`); setError(''); setMessage(''); setSelected(row.customerId);
    try {
      const r = await apiFetch<{ ok: boolean; run: EstateKnowledgeGraphRun }>(`/discovery-broker/connections/${encodeURIComponent(row.customerId)}/knowledge-graph`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const summary = r.run.summary ?? {};
      setKnowledgeGraphSample(null); setKnowledgeGraphSampleRunId(null);
      setMessage(`Canonical Estate Knowledge Graph completed for ${row.customerName}: ${String(r.run.status ?? 'unknown').toUpperCase()} · ${Number(summary.canonicalEntities ?? 0)} canonical entities · ${Number(summary.canonicalRelationships ?? 0)} canonical relationships.`);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const loadKnowledgeGraphEvidence = async (row: Connection, run: EstateKnowledgeGraphRun) => {
    if (!canViewKnowledgeGraphEvidence || !data?.estateKnowledgeGraphEnabled) return;
    setBusy(`knowledge-graph-evidence:${run.id}`); setError(''); setMessage('');
    try {
      const r = await apiFetch<{ ok: boolean; run: EstateKnowledgeGraphRun; sample: EstateKnowledgeGraphSample | null }>(`/discovery-broker/connections/${encodeURIComponent(row.customerId)}/knowledge-graph-runs/${encodeURIComponent(run.id)}?includeSample=true&sampleLimit=25`);
      setKnowledgeGraphSample(r.sample ?? { entities: [], relationships: [] });
      setKnowledgeGraphSampleRunId(run.id);
      setMessage(`Loaded bounded canonical evidence sample for ${row.customerName}. This read was recorded in the KloudSecure audit ledger.`);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  if (!data || !settings) {
    return <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">{error || 'Loading Discovery Master…'}</div>;
  }

  const sourceReadiness = selectedConnection?.lastTest?.sourceReadiness ?? {};
  const history = data.history.filter((h) => !selected || h.customerId === selected).slice(0, 10);
  const estateRuns = (data.estateRuns ?? []).filter((r) => !selected || r.customerId === selected).slice(0, 10);
  const latestEstateRun = estateRuns[0] ?? null;
  const estateSummary = latestEstateRun?.summary ?? {};
  const estateHealth = latestEstateRun?.sourceHealth ?? [];
  const knowledgeGraphRuns = (data.estateKnowledgeGraphRuns ?? []).filter((r) => !selected || r.customerId === selected).slice(0, 10);
  const latestKnowledgeGraphRun = knowledgeGraphRuns[0] ?? null;
  const knowledgeGraphSummary = latestKnowledgeGraphRun?.summary ?? {};
  const entityTypeCounts = Object.entries(knowledgeGraphSummary.entityTypeCounts ?? {}).sort((a, b) => b[1] - a[1]);
  const providerCoverageCounts = Object.entries(knowledgeGraphSummary.providerCoverageCounts ?? {}).sort((a, b) => b[1] - a[1]);
  const sourceEstateRun = estateRuns.find((run) => ['succeeded', 'partial'].includes(String(run.status).toLowerCase())) ?? null;
  const sourceRunReady = Boolean(sourceEstateRun);

  return (
    <div className="space-y-4">
      {error ? <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">{error}</div> : null}
      {message ? <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">{message}</div> : null}

      <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4">
        <div className="text-sm font-bold text-cyan-200">Discovery / Response identity isolation</div>
        <p className="mt-1 text-xs text-muted-foreground">Discovery Broker is a separate read-only secretless workload identity for broad estate enumeration. The Response Broker remains reserved for just-in-time preflight, containment and verification. Discovery does not invoke Native Response actions.</p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ['Customers', totals.total], ['Enabled', totals.enabled], ['Ready', totals.ready], ['Partial', totals.partial], ['Failed', totals.failed],
        ].map(([label, value]) => <div key={String(label)} className="rounded-xl border border-border bg-card p-3"><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 text-xl font-black">{value}</div></div>)}
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-bold">Secretless Discovery Broker Identity</div>
            <div className="text-xs text-muted-foreground">Non-secret identifiers only. Tokens/assertions stay in backend memory and are never persisted.</div>
          </div>
          <button disabled={!canManageSettings || busy === 'settings'} onClick={() => void saveSettings()} className="rounded-lg bg-cyan-500 px-3 py-2 text-xs font-bold text-slate-950 disabled:cursor-not-allowed disabled:opacity-40">{busy === 'settings' ? 'Saving…' : 'Save discovery settings'}</button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[
            ['HOME TENANT ID', 'homeTenantId'], ['UAMI CLIENT ID', 'uamiClientId'], ['UAMI PRINCIPAL/OBJECT ID', 'uamiPrincipalId'], ['UAMI RESOURCE ID', 'uamiResourceId'], ['DISCOVERY BROKER APP/CLIENT ID', 'brokerAppId'], ['DISCOVERY BROKER OBJECT ID', 'brokerObjectId'],
          ].map(([label, key]) => (
            <label key={key} className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}
              <input disabled={!canManageSettings} value={String(settings[key as keyof Settings])} onChange={(e) => setSettings((s) => s ? { ...s, [key]: e.target.value } : s)} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs normal-case text-foreground disabled:opacity-60" />
            </label>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-xs"><input disabled={!canManageSettings} type="checkbox" checked={settings.enabled} onChange={(e) => setSettings((s) => s ? { ...s, enabled: e.target.checked } : s)} />Enable native Discovery Broker authentication</label>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="border-b border-border p-4">
          <div className="text-sm font-bold">Customer Discovery Connections</div>
          <div className="text-xs text-muted-foreground">Every customer/workspace remains visible independently. This preserves workspace-specific Sentinel RBAC and Log Analytics visibility even when multiple workspaces share one Entra tenant.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1420px] w-full text-xs">
            <thead className="bg-muted/20 text-left text-[10px] uppercase tracking-wide text-muted-foreground"><tr><th className="p-3">Customer / Workspace</th><th>Enabled</th><th>Graph</th><th>MDE</th><th>Intune</th><th>Azure</th><th>Sentinel</th><th>Logs</th><th>Last test</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {data.connections.map((row) => {
                const disabled = !canManageConnections || busy === `connection:${row.customerId}`;
                const check = (key: keyof Pick<Connection,'enabled'|'graphEnabled'|'mdeEnabled'|'intuneEnabled'|'azureEnabled'|'sentinelEnabled'|'sentinelLogsEnabled'>) => <input disabled={disabled} type="checkbox" checked={Boolean(row[key])} onChange={(e) => void patchConnection(row, { [key]: e.target.checked } as Partial<Pick<Connection,'enabled'|'graphEnabled'|'mdeEnabled'|'intuneEnabled'|'azureEnabled'|'sentinelEnabled'|'sentinelLogsEnabled'>>)} />;
                return (
                  <tr key={row.customerId} onClick={() => setSelected(row.customerId)} className={`cursor-pointer border-t border-border ${selected === row.customerId ? 'bg-cyan-500/5' : 'hover:bg-muted/20'}`}>
                    <td className="p-3"><div className="font-bold text-foreground">{row.customerName}{row.homeTenant ? <span className="ml-2 text-[9px] text-cyan-300">HOME</span> : null}</div><div className="font-mono text-[10px] text-muted-foreground">{row.tenantId}</div><div className="text-[10px] text-muted-foreground">{row.workspaceName ?? 'No workspace name'} · {row.workspaceId ?? 'No workspace ID'}</div></td>
                    <td>{check('enabled')}</td><td>{check('graphEnabled')}</td><td>{check('mdeEnabled')}</td><td>{check('intuneEnabled')}</td><td>{check('azureEnabled')}</td><td>{check('sentinelEnabled')}</td><td>{check('sentinelLogsEnabled')}</td>
                    <td className="text-[10px] text-muted-foreground">{row.lastTestedAt ? new Date(row.lastTestedAt).toLocaleString() : 'Never'}</td>
                    <td>{badge(row.lastTestStatus)}</td>
                    <td><div className="flex flex-wrap gap-2"><button onClick={(e) => { e.stopPropagation(); void testConnection(row); }} disabled={!canTest || !row.enabled || Boolean(busy)} className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 font-bold text-cyan-200 disabled:cursor-not-allowed disabled:opacity-40">{busy === `test:${row.customerId}` ? 'Testing…' : 'Test discovery'}</button><button onClick={(e) => { e.stopPropagation(); void runEstateDiscovery(row); }} disabled={!canRunEstate || !data.estateRuntimeEnabled || !row.enabled || Boolean(busy)} className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-1.5 font-bold text-violet-200 disabled:cursor-not-allowed disabled:opacity-40">{busy === `estate:${row.customerId}` ? 'Discovering…' : 'Run estate'}</button></div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedConnection ? (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <div><div className="text-[10px] font-bold uppercase tracking-wider text-cyan-300">Discovery diagnostics</div><div className="text-lg font-black">{selectedConnection.customerName}</div><div className="text-xs text-muted-foreground">{selectedConnection.workspaceName ?? 'No Sentinel workspace'} · {selectedConnection.workspaceId ?? 'No workspace ID'}</div></div>
            {badge(selectedConnection.lastTestStatus)}
          </div>

          <div className="mt-4 grid gap-3 xl:grid-cols-3">
            {managedIdentityCard(selectedConnection.lastTest?.managedIdentity)}
            {providerCard('Microsoft Graph / Entra', selectedConnection.lastTest?.graph)}
            {providerCard('Microsoft Defender for Endpoint', selectedConnection.lastTest?.mde)}
            {providerCard('Microsoft Intune', selectedConnection.lastTest?.intune)}
            {providerCard('Azure Resource Graph', selectedConnection.lastTest?.azure)}
            {providerCard('Microsoft Sentinel', selectedConnection.lastTest?.sentinel)}
            {providerCard('Sentinel / Log Analytics', selectedConnection.lastTest?.sentinelLogs)}
          </div>

          <div className="mt-4 rounded-xl border border-border bg-background/40 p-3">
            <div className="text-xs font-bold">Estate Source Readiness</div>
            <div className="mt-1 text-[11px] text-muted-foreground">These checks are the source-readiness contract used by Stage 2D.3-A. READY means the Discovery Broker has the required read permission/probe for that provider; the runtime then executes only applicable workspace-table adapters.</div>
            <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {Object.entries(SOURCE_LABELS).map(([id, label]) => {
                const state = sourceReadiness[id];
                return <div key={id} className={`rounded-lg border p-2 ${state?.ready ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-border bg-muted/10'}`}><div className="flex items-center justify-between gap-2"><span className="text-xs font-bold">{label}</span>{badge(state ? (state.ready ? 'ready' : 'failed') : 'untested')}</div><div className="mt-1 text-[10px] text-muted-foreground">{state?.reason ?? 'Run Test discovery to validate.'}</div></div>;
              })}
            </div>
          </div>

          {canViewEstate && data.estateRuntimeEnabled ? (
            <div className="mt-4 rounded-xl border border-violet-500/20 bg-violet-500/5 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><div className="text-xs font-bold text-violet-200">Stage 2D.3-A · Estate Discovery Runtime</div><div className="mt-1 text-[11px] text-muted-foreground">Read-only provider inventory plus table-aware Sentinel telemetry adapters. This patch persists raw observations/relationships only; canonical entity merging remains Stage 2D.3-B.</div></div>
                <button onClick={() => void runEstateDiscovery(selectedConnection)} disabled={!canRunEstate || !selectedConnection.enabled || Boolean(busy)} className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-xs font-bold text-violet-200 disabled:cursor-not-allowed disabled:opacity-40">{busy === `estate:${selectedConnection.customerId}` ? 'Discovering estate…' : 'Run 72h estate discovery'}</button>
              </div>
              {latestEstateRun ? (
                <>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">{badge(latestEstateRun.status)}<span>Started {latestEstateRun.startedAt ? new Date(latestEstateRun.startedAt).toLocaleString() : '—'}</span><span>· {latestEstateRun.lookbackHours}h telemetry lookback</span><span>· registry <span className="font-mono text-foreground">{latestEstateRun.adapterRegistryVersion}</span></span></div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
                    {[['Observations', estateSummary.observations ?? 0], ['Relationships', estateSummary.relationships ?? 0], ['Workspace tables', estateSummary.discoveredTables ?? 0], ['Adapters ready', estateSummary.adaptersReady ?? 0], ['Partial', estateSummary.adaptersPartial ?? 0], ['Skipped', estateSummary.adaptersSkipped ?? 0], ['Errors', estateSummary.adaptersError ?? 0]].map(([label, value]) => <div key={String(label)} className="rounded-lg border border-border bg-background/50 p-2"><div className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">{label}</div><div className="mt-1 text-lg font-black">{value}</div></div>)}
                  </div>
                  <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {estateHealth.map((h) => <div key={h.adapterId} className="rounded-lg border border-border bg-background/40 p-2"><div className="flex items-center justify-between gap-2"><span className="text-xs font-bold">{h.label}</span>{badge(h.status)}</div><div className="mt-1 text-[10px] text-muted-foreground">{h.rowCount} obs · {h.relationshipCount} rel · {Math.max(0, h.durationMs)} ms{h.sourceTable ? ` · ${h.sourceTable}` : ''}</div><div className="mt-1 text-[10px] text-muted-foreground">{h.message}</div></div>)}
                  </div>
                  {latestEstateRun.diagnostics?.length ? <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-2"><div className="text-[10px] font-bold uppercase tracking-wide text-amber-300">Diagnostics</div>{latestEstateRun.diagnostics.slice(0, 6).map((d, i) => <div key={`${i}-${d.slice(0, 24)}`} className="mt-1 break-words font-mono text-[10px] text-muted-foreground">{d}</div>)}</div> : null}
                </>
              ) : <div className="mt-3 text-[11px] text-muted-foreground">No estate discovery run has been persisted for this customer yet.</div>}
            </div>
          ) : null}

          {canViewKnowledgeGraph && data.estateKnowledgeGraphEnabled ? (
            <div className="mt-4 rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-bold text-fuchsia-200">Stage 2D.3-B · Canonical Estate Knowledge Graph</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">Local-only correlation of a completed Stage 2D.3-A snapshot. Strong provider IDs are merged first; hostname/FQDN is a guarded fallback. Raw evidence remains immutable and every canonical fact keeps provenance/conflict metadata.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canViewKnowledgeGraphEvidence && latestKnowledgeGraphRun ? <button onClick={() => void loadKnowledgeGraphEvidence(selectedConnection, latestKnowledgeGraphRun)} disabled={Boolean(busy)} className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs font-bold text-cyan-200 disabled:cursor-not-allowed disabled:opacity-40">{busy === `knowledge-graph-evidence:${latestKnowledgeGraphRun.id}` ? 'Loading evidence…' : 'Load evidence sample'}</button> : null}
                  <button onClick={() => void buildKnowledgeGraph(selectedConnection)} disabled={!canBuildKnowledgeGraph || !sourceRunReady || Boolean(busy)} className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-2 text-xs font-bold text-fuchsia-200 disabled:cursor-not-allowed disabled:opacity-40">{busy === `knowledge-graph:${selectedConnection.customerId}` ? 'Correlating…' : 'Build knowledge graph'}</button>
                </div>
              </div>
              {!sourceRunReady ? <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-2 text-[11px] text-amber-200">A completed/partial Stage 2D.3-A estate run is required before canonical correlation.</div> : null}
              {latestKnowledgeGraphRun ? (
                <>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">{badge(latestKnowledgeGraphRun.status)}<span>Started {latestKnowledgeGraphRun.startedAt ? new Date(latestKnowledgeGraphRun.startedAt).toLocaleString() : '—'}</span><span>· resolver <span className="font-mono text-foreground">{latestKnowledgeGraphRun.resolverVersion}</span></span><span>· source <span className="font-mono text-foreground">{latestKnowledgeGraphRun.sourceDiscoveryRunId.slice(0, 8)}…</span></span></div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
                    {[
                      ['Raw observations', knowledgeGraphSummary.rawObservations ?? 0],
                      ['Canonical entities', knowledgeGraphSummary.canonicalEntities ?? 0],
                      ['Entity reduction', `${Number(knowledgeGraphSummary.reductionPercent ?? 0).toFixed(1)}%`],
                      ['Cross-provider', knowledgeGraphSummary.crossProviderEntities ?? 0],
                      ['Exact-ID merges', knowledgeGraphSummary.exactIdMerges ?? 0],
                      ['Hostname merges', knowledgeGraphSummary.hostnameFallbackMerges ?? 0],
                      ['Raw relationships', knowledgeGraphSummary.rawRelationships ?? 0],
                      ['Canonical relations', knowledgeGraphSummary.canonicalRelationships ?? 0],
                      ['Resolved relations', knowledgeGraphSummary.resolvedRelationships ?? 0],
                      ['Relationship resolution', `${Number(knowledgeGraphSummary.relationshipResolutionPercent ?? 0).toFixed(1)}%`],
                      ['Ambiguous candidates', knowledgeGraphSummary.ambiguousCandidates ?? 0],
                      ['Conflicts retained', knowledgeGraphSummary.conflicts ?? 0],
                    ].map(([label, value]) => <div key={String(label)} className="rounded-lg border border-border bg-background/50 p-2"><div className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">{label}</div><div className="mt-1 text-lg font-black">{value}</div></div>)}
                  </div>
                  <div className="mt-3 grid gap-3 xl:grid-cols-2">
                    <div className="rounded-lg border border-border bg-background/40 p-2"><div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Canonical entity types</div><div className="mt-2 flex flex-wrap gap-1.5">{entityTypeCounts.length ? entityTypeCounts.map(([type, count]) => <span key={type} className="rounded border border-fuchsia-500/20 bg-fuchsia-500/10 px-2 py-1 text-[10px] text-fuchsia-100"><span className="font-mono">{type}</span> · {count}</span>) : <span className="text-[10px] text-muted-foreground">No canonical entities persisted.</span>}</div></div>
                    <div className="rounded-lg border border-border bg-background/40 p-2"><div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Provider coverage</div><div className="mt-2 flex flex-wrap gap-1.5">{providerCoverageCounts.length ? providerCoverageCounts.map(([provider, count]) => <span key={provider} className="rounded border border-cyan-500/20 bg-cyan-500/10 px-2 py-1 text-[10px] text-cyan-100"><span className="font-mono">{provider}</span> · {count}</span>) : <span className="text-[10px] text-muted-foreground">No provider coverage persisted.</span>}</div></div>
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                      ['Unresolved entities', knowledgeGraphSummary.unresolvedEntities ?? 0],
                      ['Unresolved relations', knowledgeGraphSummary.unresolvedRelationships ?? 0],
                      ['Ambiguous relations', knowledgeGraphSummary.ambiguousRelationships ?? 0],
                      ['Conflicts', knowledgeGraphSummary.conflicts ?? 0],
                    ].map(([label, value]) => <div key={String(label)} className="rounded-lg border border-border bg-background/40 p-2"><div className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">{label}</div><div className="mt-1 text-base font-black">{value}</div></div>)}
                  </div>
                  {latestKnowledgeGraphRun.diagnostics?.length ? <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-2"><div className="text-[10px] font-bold uppercase tracking-wide text-amber-300">Canonicalization diagnostics</div>{latestKnowledgeGraphRun.diagnostics.slice(0, 8).map((d, i) => <div key={`${i}-${d.slice(0, 24)}`} className="mt-1 break-words font-mono text-[10px] text-muted-foreground">{d}</div>)}</div> : null}
                </>
              ) : <div className="mt-3 text-[11px] text-muted-foreground">No canonical Estate Knowledge Graph snapshot has been built for this customer yet.</div>}

              {knowledgeGraphSample && knowledgeGraphSampleRunId === latestKnowledgeGraphRun?.id ? (
                <div className="mt-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <div className="flex items-center justify-between gap-2"><div><div className="text-xs font-bold text-cyan-200">Bounded Canonical Evidence Sample</div><div className="text-[10px] text-muted-foreground">Up to 25 entities/relationships; raw evidence provenance is capped per object. Loading this sample is audit-logged.</div></div><button onClick={() => { setKnowledgeGraphSample(null); setKnowledgeGraphSampleRunId(null); }} className="rounded border border-border px-2 py-1 text-[10px] text-muted-foreground">Close</button></div>
                  <div className="mt-3 overflow-x-auto"><table className="min-w-[980px] w-full text-[10px]"><thead className="text-left uppercase tracking-wide text-muted-foreground"><tr><th className="pb-2">Entity</th><th>Type</th><th>Providers</th><th>Match</th><th>Confidence</th><th>Evidence</th><th>Conflicts</th><th>Source evidence</th></tr></thead><tbody>{knowledgeGraphSample.entities.map((entity) => <tr key={entity.id} className="border-t border-border align-top"><td className="py-2"><div className="font-bold text-foreground">{entity.displayName}</div><div className="font-mono text-[9px] text-muted-foreground">{entity.id}</div></td><td className="font-mono">{entity.entityType}</td><td>{(entity.providers ?? []).join(', ') || '—'}</td><td className="font-mono">{entity.matchStrategy ?? '—'}</td><td>{entity.confidence ?? 0}%</td><td>{entity.evidenceCount ?? 0}</td><td>{entity.conflicts?.length ?? 0}</td><td>{(entity.evidence ?? []).slice(0, 4).map((ev, i) => <div key={`${entity.id}-ev-${i}`} className="mb-1"><span className="font-mono text-cyan-200">{ev.provider ?? 'unknown'}</span> · {ev.sourceTable ?? ev.source ?? 'source'} · {ev.authority ?? 'observed'}</div>)}</td></tr>)}</tbody></table></div>
                  <div className="mt-3 overflow-x-auto"><table className="min-w-[900px] w-full text-[10px]"><thead className="text-left uppercase tracking-wide text-muted-foreground"><tr><th className="pb-2">From</th><th>Relationship</th><th>To</th><th>Providers</th><th>Confidence</th><th>Evidence</th></tr></thead><tbody>{knowledgeGraphSample.relationships.map((rel) => <tr key={rel.id} className="border-t border-border"><td className="py-2 font-mono">{rel.fromEntityId}</td><td className="font-mono text-fuchsia-200">{rel.relationshipType}</td><td className="font-mono">{rel.toEntityId}</td><td>{(rel.providers ?? []).join(', ') || '—'}</td><td>{rel.confidence ?? 0}%</td><td>{rel.evidenceCount ?? 0}</td></tr>)}</tbody></table></div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {canViewEstate && estateRuns.length ? (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-sm font-bold">Recent Estate Discovery Runs</div>
          <div className="mt-1 text-[11px] text-muted-foreground">Stage 2D.3-A retains the latest bounded raw estate snapshots per customer for Stage 2D.3-B canonical correlation.</div>
          <div className="mt-3 overflow-x-auto"><table className="min-w-[980px] w-full text-xs"><thead className="text-left text-[10px] uppercase tracking-wide text-muted-foreground"><tr><th className="pb-2">Started</th><th>Status</th><th>Lookback</th><th>Observations</th><th>Relationships</th><th>Tables</th><th>Requested by</th></tr></thead><tbody>{estateRuns.map((r) => <tr key={r.id} className="border-t border-border"><td className="py-2">{r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}</td><td>{badge(r.status)}</td><td>{r.lookbackHours}h</td><td>{Number(r.summary?.observations ?? 0)}</td><td>{Number(r.summary?.relationships ?? 0)}</td><td>{Number(r.summary?.discoveredTables ?? 0)}</td><td>{r.requestedByEmail ?? '—'}</td></tr>)}</tbody></table></div>
        </div>
      ) : null}

      {canViewKnowledgeGraph && knowledgeGraphRuns.length ? (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-sm font-bold">Recent Canonical Estate Knowledge Graph Runs</div>
          <div className="mt-1 text-[11px] text-muted-foreground">Stage 2D.3-B snapshots remain separate from immutable Stage 2D.3-A raw evidence. A later 2D.3-C patch will make MoB consume this canonical layer.</div>
          <div className="mt-3 overflow-x-auto"><table className="min-w-[1050px] w-full text-xs"><thead className="text-left text-[10px] uppercase tracking-wide text-muted-foreground"><tr><th className="pb-2">Started</th><th>Status</th><th>Canonical entities</th><th>Canonical relations</th><th>Reduction</th><th>Resolution</th><th>Cross-provider</th><th>Ambiguous</th><th>Requested by</th></tr></thead><tbody>{knowledgeGraphRuns.map((r) => <tr key={r.id} className="border-t border-border"><td className="py-2">{r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}</td><td>{badge(r.status)}</td><td>{Number(r.summary?.canonicalEntities ?? 0)}</td><td>{Number(r.summary?.canonicalRelationships ?? 0)}</td><td>{Number(r.summary?.reductionPercent ?? 0).toFixed(1)}%</td><td>{Number(r.summary?.relationshipResolutionPercent ?? 0).toFixed(1)}%</td><td>{Number(r.summary?.crossProviderEntities ?? 0)}</td><td>{Number(r.summary?.ambiguousCandidates ?? 0)}</td><td>{r.requestedByEmail ?? '—'}</td></tr>)}</tbody></table></div>
        </div>
      ) : null}

      {canViewHistory && history.length ? (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-sm font-bold">Recent Discovery Test History</div>
          <div className="mt-3 overflow-x-auto"><table className="min-w-[800px] w-full text-xs"><thead className="text-left text-[10px] uppercase tracking-wide text-muted-foreground"><tr><th className="pb-2">Completed</th><th>Status</th><th>Requested by</th><th>Tenant</th></tr></thead><tbody>{history.map((h) => <tr key={h.id} className="border-t border-border"><td className="py-2">{h.completedAt ? new Date(h.completedAt).toLocaleString() : '—'}</td><td>{badge(h.status)}</td><td>{h.requestedByEmail ?? '—'}</td><td className="font-mono text-[10px]">{h.result?.targetTenantId ?? '—'}</td></tr>)}</tbody></table></div>
        </div>
      ) : null}
    </div>
  );
}
